/**
 * Plan Configuration Service
 * 
 * Fetches plan configurations from backend to ensure frontend and backend
 * are always in sync. No more hardcoded plan limits!
 */

import { API_BASE_URL } from '../api/config';
import fetchService from './fetchService';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('PLAN_CONFIG_SERVICE');

export interface PlanConfig {
  name: string;
  display_name: string;
  description: string;
  monthly_price: number;
  billing: {
    type: 'free_credits' | 'usage_based' | 'subscription' | 'simple_subscription' | 'unlimited' | 'metered';
    monthly_free_credits?: number;
    monthly_included_credits?: number;  // For Plus/Ultra plans
    billing_threshold?: number;
    credit_limit?: number;
    requires_payment_method?: boolean;
  };
  limits: {
    entities: number;
    toolCalls: number;
    userMessages: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    acceptedEdits: number;
  };
  features: string[];
}

export interface PlanConfigResponse {
  success: boolean;
  data: {
    plans: Record<string, PlanConfig>;
    version: string;
    last_updated: string;
  };
}

class PlanConfigService {
  private cachedPlans: Record<string, PlanConfig> | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all plan configurations from backend
   */
  async getAllPlans(): Promise<Record<string, PlanConfig>> {
    // Check cache first
    if (this.cachedPlans && Date.now() < this.cacheExpiry) {
      log.log('[PlanConfigService] Using cached plan configurations');
      return this.cachedPlans;
    }

    try {
      log.log('[PlanConfigService] Fetching plan configurations from backend...');
      
      const response = await fetchService(`${API_BASE_URL}/plans/config`);
      
      if (response.success && response.data?.plans) {
        this.cachedPlans = response.data.plans;
        this.cacheExpiry = Date.now() + this.CACHE_DURATION;
        
        log.log('[PlanConfigService] ✅ Plan configurations loaded from backend', {
          planCount: Object.keys(this.cachedPlans || {}).length,
          plans: Object.keys(this.cachedPlans || {})
        });
        
        return this.cachedPlans || {};
      } else {
        throw new Error('Invalid response format from plans API');
      }
      
    } catch (error) {
      log.error('[PlanConfigService] ❌ Failed to fetch plan configurations:', error);
      
      // Fallback to hardcoded plans from plans.ts
      log.warn('[PlanConfigService] Using fallback plan configurations');
      return this.getFallbackPlans();
    }
  }

  /**
   * Get configuration for a specific plan
   */
  async getPlanConfig(planName: string): Promise<PlanConfig | null> {
    const allPlans = await this.getAllPlans();
    return allPlans[planName] || null;
  }

  /**
   * Get limits for a specific plan
   */
  async getPlanLimits(planName: string): Promise<PlanConfig['limits'] | null> {
    const planConfig = await this.getPlanConfig(planName);
    return planConfig?.limits || null;
  }

  /**
   * Get billing config for a specific plan
   */
  async getPlanBillingConfig(planName: string): Promise<PlanConfig['billing'] | null> {
    const planConfig = await this.getPlanConfig(planName);
    return planConfig?.billing || null;
  }

  /**
   * Check if a plan has unlimited usage
   */
  async isUnlimitedPlan(planName: string): Promise<boolean> {
    const billingConfig = await this.getPlanBillingConfig(planName);
    return billingConfig?.type === 'unlimited';
  }

  /**
   * Clear cache (useful for testing or when plans are updated)
   */
  clearCache(): void {
    this.cachedPlans = null;
    this.cacheExpiry = 0;
    log.log('[PlanConfigService] Plan configuration cache cleared');
  }

  /**
   * Get cached plan name synchronously (for utility functions)
   */
  getCachedPlanName(planName: string): string | null {
    if (!this.cachedPlans) {
      return null;
    }
    
    const plan = this.cachedPlans[planName];
    return plan?.name || null;
  }

  /**
   * Get cached plan config synchronously (for utility functions)
   */
  getCachedPlan(planName: string): PlanConfig | null {
    if (!this.cachedPlans) {
      return null;
    }
    
    return this.cachedPlans[planName] || null;
  }

  /**
   * Fallback plan configurations used when the /plans/config API is unreachable.
   * Must stay in sync with shared/plans.json and backend/config/constants.py.
   */
  private getFallbackPlans(): Record<string, PlanConfig> {
    log.warn('[PlanConfigService] Using minimal fallback plan configuration');

    const unlimited = { entities: -1, toolCalls: -1, userMessages: -1, inputTokens: -1, outputTokens: -1, totalTokens: -1, acceptedEdits: -1 };

    return {
      free: {
        name: 'Free',
        display_name: 'Free',
        description: '$0.50 starter budget to try River. Solo use only.',
        monthly_price: 0,
        billing: { type: 'free_credits', monthly_free_credits: 50, requires_payment_method: false },
        limits: unlimited,
        features: ['$0.50 AI budget (one-time)', 'Whiz AI model', 'Unlimited spaces', 'Community support'],
      },
      basic: {
        name: 'Basic',
        display_name: 'Basic',
        description: '$14/month. Includes $15 of River AI usage.',
        monthly_price: 14,
        billing: { type: 'simple_subscription', requires_payment_method: true },
        limits: unlimited,
        features: ['$15/month AI budget', 'All AI models', 'Unlimited spaces', 'Unlimited team members', 'Priority support'],
      },
      standard: {
        name: 'Standard',
        display_name: 'Standard',
        description: '$36/month. Includes $45 of River AI usage.',
        monthly_price: 36,
        billing: { type: 'simple_subscription', requires_payment_method: true },
        limits: unlimited,
        features: ['$45/month AI budget', 'All AI models', 'Unlimited spaces', 'Unlimited team members', 'Dedicated support'],
      },
      premium: {
        name: 'Premium',
        display_name: 'Premium',
        description: '$100/month. Includes $140 of River AI usage.',
        monthly_price: 100,
        billing: { type: 'simple_subscription', requires_payment_method: true },
        limits: unlimited,
        features: ['$140/month AI budget', 'All AI models', 'Unlimited spaces', 'Version history (forever)', 'Dedicated account manager'],
      },
      payg: {
        name: 'Pay As You Go',
        display_name: 'Pay As You Go',
        description: '$0/month. Pay only for what you use at River rates.',
        monthly_price: 0,
        billing: { type: 'metered', requires_payment_method: true },
        limits: unlimited,
        features: ['$0/month — no commitment', 'All AI models', 'Unlimited spaces', 'Pay only for what you use'],
      },
    };
  }
}

export const planConfigService = new PlanConfigService();
